import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  Divider,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { skipToken } from '@reduxjs/toolkit/query'
import { useSelector } from 'react-redux'
import { RootState } from 'store'
import { useSendCarrierLoadConfirmationEmailMutation } from 'store/load-confirmation/load-confirmation.api'
import { useLazyGetUserByEmailQuery } from 'store/auth/auth.api'
import { User } from 'store/auth/auth.types'
import { useUploadLoadConfirmationMutation } from 'store/task-automation/task-automation.api'
import {
  useGetPreSignedUrlMutation,
  useListInvoicedocumentsQuery,
} from 'store/invoices/invoices.api'
import ConfirmationDialog from 'components/confirmation-dialog/confirmation-dialog.component'
import { getLatestLoadConfirmationS3Path } from 'utils/load-confirmation-documents'
import { getUserId } from 'utils/common'
import InvoiceFromEmail from 'pages/invoice/invoice-details/invoice-table/invoice-table-body/invoice-table-email-dialog/invoice-from-email/invoice-from-email.component'
import CarrierToEmail from './carrier-to-email.component'
import CarrierEmailAttachments from './carrier-email-attachments.component'
import TranspotsLoading from 'components/transpots-loading/transpots-loading.component'
import TranspotsErrorSnackbar from 'components/transpots-error-snackbar/transpots-error-snackbar.component'
import SubmitLockButton from 'components/submit-lock-button/submit-lock-button.component'
import { CarrierLcSaveResult } from './use-send-to-carrier'

type CarrierEmailDialogProps = {
  open: boolean
  onClose: () => void
  carrierContext: CarrierLcSaveResult
  onSent: () => void
}

const CarrierEmailDialog = ({
  open,
  onClose,
  carrierContext,
  onSent,
}: CarrierEmailDialogProps) => {
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'))
  const userId = getUserId()
  const user = useSelector((state: RootState) => state.auth.user)

  const [recipients, setRecipients] = useState('')
  const [errorOnSending, setErrorOnSending] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [checkingCarrier, setCheckingCarrier] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [matchedUsers, setMatchedUsers] = useState<User[]>([])
  const [emailSent, setEmailSent] = useState(false)
  const sendingRef = useRef(false)

  const handleRecipientsChange = useCallback((value: string) => {
    setRecipients(value)
  }, [])

  useEffect(() => {
    if (!open) return
    setEmailSent(false)
    setConfirmOpen(false)
    setMatchedUsers([])
    setErrorOnSending(false)
    setErrorMessage('')
    sendingRef.current = false
  }, [open])

  const [sendEmail, { isLoading }] = useSendCarrierLoadConfirmationEmailMutation()
  const [getUserByEmail] = useLazyGetUserByEmailQuery()
  const [getPreSignedUrl] = useGetPreSignedUrlMutation()
  const [uploadLoadConfirmation] = useUploadLoadConfirmationMutation()

  const { data: orderDocuments } = useListInvoicedocumentsQuery(
    open && carrierContext.carrierOrderId
      ? { orderId: String(carrierContext.carrierOrderId) }
      : skipToken
  )

  const latestLcS3Path = useMemo(() => {
    const fromDocuments = getLatestLoadConfirmationS3Path(orderDocuments?.LC)
    return fromDocuments || carrierContext.s3Path || ''
  }, [carrierContext.s3Path, orderDocuments?.LC])

  const senderName = useMemo(() => {
    const companyName = user?.companyName?.trim()
    if (companyName) return companyName
    return user?.email?.trim() ?? ''
  }, [user?.companyName, user?.email])

  const subject = useMemo(
    () =>
      `Load Confirmation ${carrierContext.loadNumber} from ${senderName}`,
    [carrierContext.loadNumber, senderName]
  )

  const message = useMemo(
    () =>
      `Please find attached the load confirmation for load# ${carrierContext.loadNumber}.`,
    [carrierContext.loadNumber]
  )

  const userEmail = user?.email?.trim() || null

  const platformNames = useMemo(() => {
    const names = matchedUsers
      .map((matched) => matched.companyName?.trim() || matched.email)
      .filter(Boolean)
    return names.join(' and ')
  }, [matchedUsers])

  const finishAfterEmail = () => {
    onSent()
    onClose()
  }

  const handleDialogClose = () => {
    if (emailSent) {
      finishAfterEmail()
      return
    }
    onClose()
  }

  const findPlatformUsers = async (recipientList: string[]) => {
    const currentEmail = userEmail?.toLowerCase()
    const found: User[] = []
    const seen = new Set<string>()

    for (const email of recipientList) {
      const normalized = email.trim().toLowerCase()
      if (!normalized || normalized === currentEmail) continue

      const result = await getUserByEmail({ email: email.trim() })
      if (result.error) {
        const status = (result.error as { status?: number | string }).status
        if (status === 404) continue
        throw new Error(
          'Could not verify the carrier email. Please try again.'
        )
      }
      const matched = result.data
      if (!matched?.id || matched.id === userId || seen.has(matched.id)) continue
      seen.add(matched.id)
      found.push(matched)
    }

    return found
  }

  const uploadCreatedLoadConfirmation = (
    s3Path: string,
    carrierUsers: User[]
  ) => {
    void (async () => {
      try {
        const presigned = await getPreSignedUrl({ s3URL: s3Path }).unwrap()
        const pdfResponse = await fetch(presigned.preSignedURL)
        if (!pdfResponse.ok) {
          throw new Error('Could not read the load confirmation file.')
        }
        const blob = await pdfResponse.blob()
        const file = new File([blob], `LC_${carrierContext.loadNumber}.pdf`, {
          type: 'application/pdf',
        })
        await Promise.all(
          carrierUsers.map((matched) =>
            uploadLoadConfirmation({
              file,
              userId: matched.id,
            }).unwrap()
          )
        )
      } catch (error) {
        console.error(
          'Failed to upload load confirmation to the carrier account',
          error
        )
      }
    })()
  }

  const deliver = async (carrierUsers: User[]) => {
    if (sendingRef.current || emailSent) return
    sendingRef.current = true
    setErrorOnSending(false)

    try {
      await sendEmail({
        userId: userId ?? '',
        recipient: recipients.trim(),
        subject,
        message,
        s3Urls: latestLcS3Path ? [latestLcS3Path] : [],
      }).unwrap()
      setEmailSent(true)

      if (carrierUsers.length > 0 && latestLcS3Path) {
        uploadCreatedLoadConfirmation(latestLcS3Path, carrierUsers)
      }

      finishAfterEmail()
    } catch (error: unknown) {
      setErrorOnSending(true)
      const err = error as { error?: string; data?: { message?: string } }
      setErrorMessage(
        err?.data?.message ?? err?.error ?? 'Failed to send email.'
      )
    } finally {
      sendingRef.current = false
    }
  }

  const handleSend = async () => {
    const recipientList = recipients
      .split(',')
      .map((email) => email.trim())
      .filter(Boolean)

    if (recipientList.length === 0) {
      setErrorOnSending(true)
      setErrorMessage('Please enter a valid dispatch email address.')
      return
    }

    setCheckingCarrier(true)
    try {
      const matches = await findPlatformUsers(recipientList)
      if (matches.length > 0) {
        setMatchedUsers(matches)
        setConfirmOpen(true)
        return
      }
      await deliver([])
    } catch (error: unknown) {
      setErrorOnSending(true)
      const err = error as { message?: string }
      setErrorMessage(
        err?.message ?? 'Could not verify the carrier email. Please try again.'
      )
    } finally {
      setCheckingCarrier(false)
    }
  }

  const handleConfirmYes = async () => {
    const usersToCreate = matchedUsers
    setConfirmOpen(false)
    await deliver(usersToCreate)
  }

  const handleConfirmNo = async () => {
    setConfirmOpen(false)
    await deliver([])
  }

  return (
    <Dialog
      fullScreen={fullScreen}
      open={open}
      onClose={handleDialogClose}
      fullWidth
      maxWidth="md"
    >
      <Box>
        <Typography sx={{ bgcolor: 'secondary.light', p: 2 }}>
          Send by Email / {carrierContext.loadNumber}
        </Typography>

        <List>
          <InvoiceFromEmail />
          <CarrierToEmail
            open={open}
            defaultEmail={carrierContext.dispatchEmail}
            errorOnSending={errorOnSending}
            userEmail={userEmail}
            onRecipientsChange={handleRecipientsChange}
          />
          <ListItem>
            <ListItemText>Subject: {subject}</ListItemText>
          </ListItem>
          <Divider />
          <ListItem>
            <ListItemText>Message: {message}</ListItemText>
          </ListItem>
        </List>

        <CarrierEmailAttachments carrierContext={carrierContext} />

        <Stack
          direction="row"
          justifyContent="center"
          p={2}
          spacing={2}
          sx={{ borderTop: '1px solid #D9D9D9' }}
        >
          <Button
            variant="contained"
            sx={{
              borderRadius: '6px',
              textTransform: 'capitalize',
              fontWeight: 700,
              bgcolor: 'secondary.main',
              minWidth: '150px',
            }}
            onClick={handleDialogClose}
          >
            Cancel
          </Button>
          <SubmitLockButton
            variant="contained"
            sx={{
              borderRadius: '6px',
              textTransform: 'capitalize',
              fontWeight: 700,
              bgcolor: 'primary.main',
              minWidth: '150px',
            }}
            onClick={handleSend}
            disabled={isLoading || checkingCarrier || emailSent}
          >
            Send to Carrier
          </SubmitLockButton>
        </Stack>
      </Box>
      {(isLoading || checkingCarrier) && (
        <TranspotsLoading
          loadingText={
            checkingCarrier ? 'Checking carrier...' : 'Sending Email...'
          }
        />
      )}
      <ConfirmationDialog
        open={confirmOpen}
        title="Upload load confirmation"
        handleClose={() => setConfirmOpen(false)}
        handleCancelBtn={handleConfirmNo}
        handleSuccessBtn={handleConfirmYes}
        cancelBtnText="No"
        successBtnText="Yes"
        maxWidth="sm"
        fullWidth
      >
        {`Upload this load confirmation to the ${platformNames} Transpots account? The order and invoice will be created there automatically.`}
      </ConfirmationDialog>
      <TranspotsErrorSnackbar
        message={errorMessage}
        open={errorOnSending}
        handleClose={() => setErrorOnSending(false)}
      />
    </Dialog>
  )
}

export default CarrierEmailDialog
